import { MediaObjectUrl } from '@kudos/http-client'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import UserAvatar from '@shared/avatar/UserAvatar'
import MemberProfilePreviewWrapper from '@shared/memberProfilePreview/MemberProfilePreviewWrapper'
import { TFunction } from '@shared/types/types'
import { fullNameFromParts } from '@shared/utils/nameFormatting'
import { T, useT } from '@transifex/react'
import { isEmpty } from 'lodash/fp'
import { FC, ReactElement, ReactNode, Ref, forwardRef } from 'react'
import { Link as RouterLink } from 'react-router'

/**
 * Structural shape that every user-displayable type in the app conforms to.
 *
 * Concrete types satisfy this without casts:
 *   - `TenantMember` (firstName/lastName/email?/avatarUrls?)
 *   - `StaffingRecord` (firstName/lastName/email/avatarUrl/active)
 *   - `TenantMemberDisplayData` / `TimelineTenantMemberDisplayData`
 *   - `UserInfo` (auditing) and other `*DisplayData` embed shapes
 *   - bare `{ firstName, lastName }` literals
 *
 * The component normalizes `avatarUrls`/`avatarUrl` and `active`/`isActive`
 * internally so callers don't need to massage their objects.
 */
export type DisplayableUser = {
  /**
   * Primary id of the record. For `TenantMember`/`*DisplayData` shapes this IS
   * the Kudos member id. For a `StaffingRecord` (HRIS row) it is the staff
   * record id — NOT the linked member id — so avatar hashing must not use it
   * directly. See {@link resolveAvatarMemberId}.
   */
  id?: string | null
  /**
   * Linked Kudos member id when the record is a `StaffingRecord` whose employee
   * is already a member. Absent on `TenantMember`/`*DisplayData` shapes (their
   * `id` already is the member id). Used to keep default avatars stable between
   * staff tables and the rest of the app.
   */
  tenantMemberId?: string | null
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  email?: string | null
  avatarUrls?: MediaObjectUrl | null
  avatarUrl?: string | null
  active?: boolean
  isActive?: boolean
  /**
   * Kudos API entity discriminator (e.g. `'tenantMember'`, `'staffRecord'`).
   * When present, it is the authoritative signal used by
   * {@link isStaffingRecord} to decide whether to suppress the
   * "(archived)" suffix. All API-returned entities include it; ad-hoc
   * literals should set it explicitly when they need to mimic a
   * `StaffingRecord` (otherwise they're treated as TenantMember-like).
   */
  __entity_type__?: string
}

export type UserNameDisplayProps = {
  /**
   * The user to display.
   *
   *   - `undefined` — no data available yet (e.g. async load in progress, or
   *     an optional field that wasn't provided). Renders nothing so the
   *     surrounding layout collapses cleanly without a flash of placeholder
   *     text.
   *   - `null` — the user was explicitly deleted upstream. Renders the
   *     localized "Deleted user" placeholder.
   *   - an object with no usable identity (every name field empty) renders
   *     the localized "No name" placeholder.
   */
  user: DisplayableUser | null | undefined
  /** Render a `UserAvatar` next to the name. Defaults to `false`. Suppressed for deleted users. */
  showAvatar?: boolean
  /** When set and the user is live, the name renders as a router `<Link>`. */
  linkTo?: string
  /** Fires when the name (link or plain text) is clicked. Not wired for deleted users. */
  onClick?: () => void
  /** Optional secondary line shown below the name; switches the layout from inline to stacked. Suppressed for deleted users. */
  subText?: ReactNode
  /** Style override applied to the name `<Link>`/`<Typography>`. */
  sx?: SxProps
  /**
   * When `true`, the rendered `<Link>` uses `color="inherit"` instead of the theme primary
   * (`colors.bluePrimary`). Set this on wall feed surfaces (posts, comments, recipients,
   * senders) so the user name blends into the surrounding feed text. Defaults to `false`.
   */
  inheritLinkColor?: boolean
  /**
   * When `true`, hovering the rendered name opens the member profile preview card
   * (same behavior `UserAvatar` provides for avatars). Opt-in: **defaults to `false`**
   * so the wrapper is only attached on the surfaces that want it (home/profile name
   * hovers). The preview is additionally gated by member id, self, archived state, and
   * the route/desktop/current-member guard inside {@link MemberProfilePreviewWrapper}.
   */
  enableProfilePreview?: boolean
}

/**
 * Resolves the member id that a default avatar should be hashed from.
 *
 * Prefers `tenantMemberId` (present on a `StaffingRecord` whose employee is
 * already a Kudos member) over `id`. This ensures a staff member's default
 * avatar in HRIS/staff tables matches the one shown on their profile, feeds,
 * and search — all of which key off the Kudos member id. For `TenantMember` /
 * `*DisplayData` shapes, `tenantMemberId` is absent and `id` (already the
 * member id) is used. For unlinked staff records, `id` (the staff record id)
 * is a stable fallback since there is no member avatar to stay consistent with.
 */
export const resolveAvatarMemberId = (user: DisplayableUser): string | null | undefined =>
  user.tenantMemberId ?? user.id

/**
 * `true` when there is no usable identity to display: the user is `null`/`undefined`
 * OR every identifying field (`firstName`, `lastName`, `fullName`) is empty.
 */
export const isMissingName = (user: DisplayableUser): boolean => {
  return isEmpty(user.firstName) && isEmpty(user.lastName) && isEmpty(user.fullName)
}

/**
 * Detects whether `user` originated from a `StaffingRecord` (HRIS) vs a
 * `TenantMember` / `*DisplayData` (platform identity).
 *
 * The distinction matters for the "(archived)" suffix: on a `StaffingRecord`,
 * `active: false` means the employee has been terminated in the HRIS, which
 * is not the same as being archived in the Kudos platform. We deliberately
 * do not surface "(archived)" in that case.
 *
 * Detection strategy:
 *   1. **Authoritative** — the API entity discriminator `__entity_type__`.
 *      Every payload returned by `@kudos/http-client` carries this tag
 *      (`'staffRecord'`, `'tenantMember'`, etc.), and it survives spreads,
 *      partial embeds, and optional-field omissions. When present we trust
 *      it exclusively.
 *   2. **Fallback** — for ad-hoc literals that don't carry an entity tag,
 *      we look at multiple structural cues: the presence of singular
 *      `avatarUrl` AND the absence of plural `avatarUrls` AND the absence
 *      of any TenantMember-only field (`isActive`, `id`-with-`active`).
 *      Combining cues protects against a stray `avatarUrl` accidentally
 *      flipping a TenantMember-shaped literal into "StaffingRecord" — and
 *      vice versa for a partial StaffingRecord embed missing `avatarUrl`.
 *      Bare/ambiguous literals default to TenantMember-like (i.e.
 *      "(archived)" applies if `active`/`isActive` is `false`).
 */
const isStaffingRecord = (user: DisplayableUser): boolean => {
  if (user.__entity_type__ !== undefined) {
    return user.__entity_type__ === 'staffRecord'
  }
  const hasAvatarUrl = 'avatarUrl' in user
  const hasAvatarUrls = 'avatarUrls' in user
  const hasTenantMemberOnlyField = 'isActive' in user
  return hasAvatarUrl && !hasAvatarUrls && !hasTenantMemberOnlyField
}

/**
 * `true` when the user should display the localized "(archived)" suffix.
 * Both `isActive: false` (TenantMember archival) and `active: false`
 * (TenantMember*DisplayData archival) qualify. `StaffingRecord`s are
 * intentionally excluded — see {@link isStaffingRecord}.
 */
const isArchivedUser = (user: DisplayableUser): boolean => {
  if (isStaffingRecord(user)) return false
  return user.isActive === false || user.active === false
}

/**
 * String-returning counterpart to `<UserNameDisplay>` for non-JSX contexts:
 * page titles, document titles, toasts, aria-labels, etc.
 *
 *   - `undefined` user — returns an empty string. This represents "no data
 *     available yet" (async load in progress, optional field omitted) and
 *     should not surface a placeholder, otherwise consumers like
 *     `setPageTitle` would briefly render "Deleted user" while a request is
 *     in flight.
 *   - `null` user — returns the localized "Deleted user" label. This
 *     represents an upstream entity that was explicitly removed.
 *   - an object with no usable identity returns the localized "No name".
 *   - otherwise returns the resolved name (with "(archived)" suffix when
 *     applicable).
 */
export const getUserDisplayName = (
  user: DisplayableUser | null | undefined,
  t: TFunction,
): string => {
  if (user === undefined) return ''
  if (user === null) return t('Deleted user')
  if (isMissingName(user)) return t('No name')
  const fullName = user.fullName || fullNameFromParts(user.firstName, user.lastName)
  const base = fullName || user.email || ''
  return isArchivedUser(user) ? `${base} ${t('(archived)')}` : base
}

type NormalizedUser = {
  /** Member id for default-avatar hashing; see {@link resolveAvatarMemberId}. */
  id?: string | null
  firstName: string | null | undefined
  lastName: string | null | undefined
  fullName: string | null | undefined
  email: string | null | undefined
  avatarMedia: MediaObjectUrl | null
  avatarSrc: string | undefined
  /** Drives avatar styling (greyed-out for inactive users). Combines both flags. */
  isActive: boolean
  /**
   * Drives the "(archived)" suffix on the rendered name. `StaffingRecord`
   * users are excluded even if `active: false` — see {@link isArchivedUser}.
   */
  isArchived: boolean
}

const normalizeUser = (user: DisplayableUser): NormalizedUser => ({
  id: resolveAvatarMemberId(user),
  firstName: user.firstName,
  lastName: user.lastName,
  fullName: user.fullName,
  email: user.email,
  avatarMedia: user.avatarUrls ?? null,
  avatarSrc: user.avatarUrl ?? undefined,
  isActive: user.isActive ?? user.active ?? true,
  isArchived: isArchivedUser(user),
})

/** Resolves the user's display name without any archived suffix. */
const resolveBaseText = (user: NormalizedUser): string => {
  const fullName = user.fullName || fullNameFromParts(user.firstName, user.lastName)
  return fullName || user.email || ''
}

const DeletedUserText: FC<{ sx?: SxProps }> = ({ sx }) => (
  <Typography component="span" variant="inherit" sx={sx}>
    <T _str="Deleted user" />
  </Typography>
)

const NoNameText: FC<{ sx?: SxProps }> = ({ sx }) => (
  <Typography component="span" variant="inherit" sx={sx}>
    <T _str="No name" />
  </Typography>
)

const UserNameDisplay = forwardRef<HTMLElement, UserNameDisplayProps>(
  (
    {
      user,
      showAvatar = false,
      linkTo,
      onClick,
      subText,
      sx,
      inheritLinkColor = false,
      enableProfilePreview = false,
      ...rest
    },
    ref,
  ) => {
    const t = useT()

    if (user === undefined) {
      return null
    }

    if (user === null) {
      return <DeletedUserText sx={sx} />
    }

    if (isMissingName(user)) {
      return <NoNameText sx={sx} />
    }

    const normalized = normalizeUser(user as DisplayableUser)
    const baseText = resolveBaseText(normalized)

    const avatarNode = showAvatar ? (
      <UserAvatar
        tenantMemberId={normalized?.id}
        firstName={normalized.firstName}
        lastName={normalized.lastName}
        fullName={normalized.fullName}
        media={normalized.avatarMedia}
        src={normalized.avatarSrc}
        isActive={normalized.isActive}
      />
    ) : null

    const attachRefToName = !subText && !avatarNode

    // Archived users are intentionally de-emphasized: never linked, always
    // plain non-bold text. Caller-supplied `sx` is preserved for layout
    // concerns (overflow, color, etc.) but `fontWeight` is forced to `normal`
    // to override any bold styling from the surrounding context.
    const renderArchivedName = () => (
      <Typography
        ref={attachRefToName ? ref : undefined}
        {...(attachRefToName ? rest : {})}
        component="span"
        variant="inherit"
        translate="no"
        sx={[...(Array.isArray(sx) ? sx : [sx]), { fontWeight: 'normal' }]}>
        {baseText} {t('(archived)')}
      </Typography>
    )

    const renderLinkedName = () => (
      <Link
        ref={attachRefToName ? (ref as Ref<HTMLAnchorElement>) : undefined}
        {...(attachRefToName ? rest : {})}
        component={RouterLink}
        to={linkTo as string}
        onClick={onClick}
        translate="no"
        variant="inherit"
        // The platform standard for user name links is `bluePrimary` (theme `primary.main`).
        // Wall feed surfaces opt out via `inheritLinkColor` so the name blends with feed text.
        color={inheritLinkColor ? 'inherit' : 'primary'}
        sx={sx}>
        {baseText}
      </Link>
    )

    const renderPlainName = () => (
      <Typography
        ref={attachRefToName ? ref : undefined}
        {...(attachRefToName ? rest : {})}
        component="span"
        variant="inherit"
        translate="no"
        onClick={onClick}
        sx={sx}>
        {baseText}
      </Typography>
    )

    let nameNode: ReactElement
    if (normalized.isArchived) {
      nameNode = renderArchivedName()
    } else if (linkTo) {
      nameNode = renderLinkedName()
    } else {
      nameNode = renderPlainName()
    }

    let content: ReactElement
    if (subText) {
      content = (
        <Stack
          ref={ref as Ref<HTMLDivElement>}
          {...rest}
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            alignItems: 'center',
          }}>
          {avatarNode}
          <Stack
            sx={{
              minWidth: 0,
            }}>
            {nameNode}
            <Typography data-testid="user-sub-text" variant="caption">
              {subText}
            </Typography>
          </Stack>
        </Stack>
      )
    } else if (!avatarNode) {
      content = nameNode
    } else {
      content = (
        <Stack
          ref={ref as Ref<HTMLDivElement>}
          {...rest}
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            alignItems: 'center',
          }}>
          {avatarNode}
          {nameNode}
        </Stack>
      )
    }

    // Attach the hover profile-preview only where the caller opted in and the
    // user is a live member with an id. Deleted/no-name users already returned
    // earlier and archived users are excluded here, so they are never wrapped.
    // Skipped when `showAvatar` is set: those surfaces preview via the avatar
    // (`UserAvatar`) instead, so the name must not add a second hover target.
    // `normalized.id` is the resolved member id (see resolveAvatarMemberId).
    // The route/desktop/current-member and skip-self gates live inside
    // MemberProfilePreviewWrapper (same guard UserAvatar relies on).
    const previewMemberId = normalized.id
    const showPreview =
      enableProfilePreview && !showAvatar && Boolean(previewMemberId) && !normalized.isArchived

    if (showPreview) {
      return (
        <MemberProfilePreviewWrapper tenantMemberId={previewMemberId}>
          {content}
        </MemberProfilePreviewWrapper>
      )
    }

    return content
  },
)

UserNameDisplay.displayName = 'UserNameDisplay'

export default UserNameDisplay
